import {
    type ConfigurablePolicy,
    type NewUserPolicy,
    type ProgramStatus,
    REGION_OPTIONS,
    type WalletPolicy
} from "../shared/programMocks";

/** Status → IndicationUnit state (green = active, yellow = draft, grey = ended). */
export const STATUS_STATE: Record<ProgramStatus, "success" | "pending" | "disabled"> = {
    active: "success",
    in_progress: "pending",
    ended: "disabled"
};

/** Status → label text-color utility. */
export const STATUS_TEXT: Record<ProgramStatus, string> = {
    active: "text-success-base",
    in_progress: "text-warning-base",
    ended: "text-foreground-muted"
};

/** Human label for a region value (falls back to the raw value). */
export function regionLabelOf(region: string): string {
    return REGION_OPTIONS.find((r) => r.value === region)?.label ?? region;
}

// ─── Policy value parse/serialize ────────────────────────────────────────
// Shared by the create-policy editor (PolicyCreator), the per-policy inline
// edit (PolicyRow), the tranche inputs (BalanceAmountTranche / TimeHeldTranche),
// and the wallet-list config (WalletListInput). Verbatim move from the former
// policyConfiguration.tsx — not a redesign.

// ─── Wallet-policy config (3-way mode + address list) ────────────────────
export type WalletMode = "whitelist" | "blacklist" | "kyc-only";

export interface WalletConfig {
    /** Whitelist (only listed qualify), Blacklist (all but listed qualify),
     *  or KYC Only (eligibility gated on KYC-approved status — the manual
     *  address list is unused). */
    mode: string;
    addresses: string[];
}

/** Parse a wallet policy's stored value (JSON `{mode, addresses}`), tolerating
 *  the legacy newline-joined-addresses form. Back-compat: an old value carrying
 *  `kycOnly: true` is folded into `mode: "kyc-only"`. */
export function parseWalletConfig(value: number | string | undefined, fallbackMode: string): WalletConfig {
    if (typeof value === "string" && value.trim().startsWith("{")) {
        try {
            const parsed = JSON.parse(value) as Partial<WalletConfig> & { kycOnly?: boolean };
            const rawMode = typeof parsed.mode === "string" ? parsed.mode : fallbackMode;
            // Legacy shape: `kycOnly: true` on any mode ⇒ the new KYC-only mode.
            const mode = parsed.kycOnly === true ? "kyc-only" : rawMode;
            return {
                mode,
                addresses: Array.isArray(parsed.addresses)
                    ? parsed.addresses.filter((a): a is string => typeof a === "string")
                    : []
            };
        } catch {
            // fall through to legacy parsing
        }
    }
    const addresses = typeof value === "string" && value ? value.split("\n").filter(Boolean) : [];
    return { mode: fallbackMode, addresses };
}

export function serializeWalletConfig(config: WalletConfig): string {
    return JSON.stringify(config);
}

/** Active description for a wallet policy — the KYC copy in `kyc-only` mode,
 *  else the selected mode's copy when it has modes, else its static
 *  description. */
export function walletDescription(policy: WalletPolicy, value: number | string | undefined): string {
    if (policy.modes && policy.modes.length > 0) {
        const { mode } = parseWalletConfig(value, policy.modes[0].value);
        if (mode === "kyc-only") {
            return "Only KYC-approved wallets qualify.";
        }
        return policy.modes.find((m) => m.value === mode)?.description ?? policy.description;
    }
    return policy.description;
}

// ─── Tranche-policy config (threshold amount + reward multiplier rows) ───
export interface Tranche {
    /** Threshold amount. `null` = empty (not yet typed) — rendered blank. */
    amount: number | null;
    /** Reward multiplier. `null` = empty (not yet typed) — rendered blank. */
    multiplier: number | null;
}

/** Coerce a stored tranche field to a number, preserving EMPTY (`null`). */
function trancheField(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

/** Parse a tranche policy's stored value (JSON array of `{amount, multiplier}`).
 *  Empty fields are preserved as `null` so the editor renders a blank,
 *  user-fillable row; read-path consumers coalesce `null → 0` at display. */
export function parseTranches(value: number | string | undefined): Tranche[] {
    if (typeof value === "string" && value.trim().startsWith("[")) {
        try {
            const arr = JSON.parse(value) as unknown[];
            if (Array.isArray(arr)) {
                return arr.map((t) => {
                    const obj = (t ?? {}) as Partial<Record<keyof Tranche, unknown>>;
                    return {
                        amount: trancheField(obj.amount),
                        multiplier: trancheField(obj.multiplier)
                    };
                });
            }
        } catch {
            // fall through
        }
    }
    return [];
}

export function serializeTranches(tranches: Tranche[]): string {
    return JSON.stringify(tranches);
}

// ─── New-User-policy config (one-time signup bonus + recency window) ──────
export interface NewUserConfig {
    /** One-time USDv payout per eligible user. `null` = empty (not yet typed). */
    bonusAmount: number | null;
    /** Eligibility window in days — "joined within N days". `null` = empty. */
    joinedWithinDays: number | null;
}

/** Parse a new-user policy's stored value (JSON `{bonusAmount, joinedWithinDays}`).
 *  EMPTY-PRESERVING: a missing / non-finite / null field stays `null` so the
 *  editor renders the fields BLANK; the read-path coalesces `null → default`. */
export function parseNewUserConfig(value: number | string | undefined): NewUserConfig {
    if (typeof value === "string" && value.trim().startsWith("{")) {
        try {
            const parsed = JSON.parse(value) as Partial<Record<keyof NewUserConfig, unknown>>;
            const toField = (raw: unknown): number | null => {
                if (raw === null || raw === undefined || raw === "") return null;
                const n = Number(raw);
                return Number.isFinite(n) ? n : null;
            };
            return {
                bonusAmount: toField(parsed.bonusAmount),
                joinedWithinDays: toField(parsed.joinedWithinDays)
            };
        } catch {
            // fall through to empty
        }
    }
    return { bonusAmount: null, joinedWithinDays: null };
}

export function serializeNewUserConfig(config: NewUserConfig): string {
    return JSON.stringify(config);
}

/** Active read-view description for a new-user policy — the one-time bonus
 *  amount + the recency window. Consume-once is implied (backend-enforced). */
export function newUserDescription(policy: NewUserPolicy, value: number | string | undefined): string {
    const parsed = parseNewUserConfig(value);
    // Read-path: an empty (null) field falls back to the policy default.
    const bonusAmount = parsed.bonusAmount ?? policy.defaultBonusAmount;
    const joinedWithinDays = parsed.joinedWithinDays ?? policy.defaultJoinedWithinDays;
    return `One-time ${bonusAmount.toLocaleString("en-US")} USDv bonus for users who joined within ${joinedWithinDays.toLocaleString(
        "en-US"
    )} day${joinedWithinDays === 1 ? "" : "s"}.`;
}

/** Initial editor value for a freshly-chosen policy type — every fillable
 *  numeric field starts EMPTY so the user types it in (no seeded default).
 *  Wallet configs keep the default mode + empty address list; select keeps its
 *  catalog default (no empty state). Mirrors the shared `policyFixtures`. */
export function initialValueFor(available: ConfigurablePolicy[], id: string): number | string {
    const next = available.find((p) => p.id === id);
    if (!next) return "";
    if (next.kind === "wallet") {
        return serializeWalletConfig({
            mode: next.modes?.[0]?.value ?? "",
            addresses: []
        });
    }
    if (next.kind === "tranche") {
        // Start from a single COMPLETE base tranche — the sensible "everyone at
        // 1×" base tier (amount 0, multiplier 1). Further tiers are ADDED via
        // +Add Tranche and start EMPTY ({ null, null }) for the user to fill.
        return serializeTranches([{ amount: 0, multiplier: 1 }]);
    }
    if (next.kind === "new-user") {
        // Both fields blank — the user types the bonus + recency window.
        return serializeNewUserConfig({ bonusAmount: null, joinedWithinDays: null });
    }
    if (next.kind === "slider") {
        // Empty amount field — the user types the threshold in.
        return "";
    }
    return next.defaultValue;
}

/** Whether a policy's in-progress editor value has every required fillable
 *  field filled — the gate for enabling "Add Policy". Mirrors the shared
 *  `policyFixtures.isPolicyValueComplete`. */
export function isPolicyValueComplete(policy: ConfigurablePolicy, value: number | string): boolean {
    if (policy.kind === "slider") {
        if (value === "" || value === null || value === undefined) return false;
        const n = Number(value);
        return Number.isFinite(n) && n > 0;
    }
    if (policy.kind === "new-user") {
        const { bonusAmount, joinedWithinDays } = parseNewUserConfig(value);
        return bonusAmount !== null && bonusAmount > 0 && joinedWithinDays !== null && joinedWithinDays > 0;
    }
    if (policy.kind === "tranche") {
        const tranches = parseTranches(value);
        return tranches.some((t) => t.amount !== null && t.amount >= 0 && t.multiplier !== null && t.multiplier > 0);
    }
    return true;
}
